"use client";

import React from "react";
import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import Layout from "@/src/components/layout";
import SEO from "@/src/shared/components/SEO";
import FooterMain from "@/src/components/layout/footer";
import {
  HostDetails,
  hostService,
  useHostDetailsQuery,
  type IHostProfile,
} from "@/src/features/host";
import { APP_URL } from "@/src/core/config/app";

interface IHostDetailsPageProps {
  hostData?: IHostProfile | null;
}

const HostDetailsView: React.FC<IHostDetailsPageProps> = ({ hostData }) => {
  const router = useAppRouter();
  const hostId = hostService.parseHostId(router.query.host_id);

  const { data } = useHostDetailsQuery(hostId ?? 0);
  const effectiveHost = hostData || data?.data || null;

  const seo = hostService.buildSeo(effectiveHost, hostId);
  const pageTitle = seo.title;
  const dynamicHostDescription = seo.description;
  const avatarUrl = effectiveHost ? hostService.avatarUrl(effectiveHost) : null;
  const seoImages = avatarUrl
    ? [
        {
          url: avatarUrl,
          width: 1200,
          height: 630,
          alt: effectiveHost ? hostService.fullName(effectiveHost) : "Host",
        },
      ]
    : [
        {
          url: `${APP_URL}/images/small-logo.png`,
          width: 800,
          height: 600,
          alt: "Travela",
        },
      ];

  const canonicalPath = seo.canonical;

  return (
    <>
      <SEO
        title={pageTitle}
        description={dynamicHostDescription}
        canonical={canonicalPath}
        url={canonicalPath}
        keywords={[
          `Host ${effectiveHost ? hostService.fullName(effectiveHost) : ""}`,
          "Professional Host",
          "Travela Host Profiles",
        ]}
        siteUrl={APP_URL}
        siteName="Travela"
        locale="en_US"
        images={seoImages}
        ogType={effectiveHost ? "profile" : "website"}
        jsonLd={
          effectiveHost
            ? {
                "@context": "https://schema.org",
                "@type": "Person",
                name: hostService.fullName(effectiveHost),
                image: seoImages[0]?.url,
                url: canonicalPath,
                description: dynamicHostDescription,
              }
            : undefined
        }
      />
      <Layout>
        <HostDetails />
        <FooterMain />
      </Layout>
    </>
  );
};

export default HostDetailsView;
